// src/App.jsx
import { Suspense } from 'react';
import { Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import Layout from './Layout.jsx';
import MaintenanceGate from './components/maintenance/MaintenanceGate.jsx';
import PublicationGate from './components/maintenance/PublicationGate.jsx';
import ProtectedRoute from './components/auth/ProtectedRoute.jsx';
import AdminMfaBoundary from './components/auth/AdminMfaBoundary.jsx';
import Landing from './pages/Landing.jsx';
import { useTranslation } from './components/i18n/TranslationProvider.jsx';
import { SUPPORT_FAQ_CATEGORY_ORDER, buildFaqCategoryPath } from './lib/supportFaqRouting.js';
import {
  PUBLIC_AUTH_CALLBACK_PATH,
  STAFF_LOGIN_PATH,
  STAFF_RESET_PATH,
} from '../shared/publicationAccess.js';
import { lazyRouteWithRecovery } from './utils/lazyRouteWithRecovery.jsx';

const Dashboard = lazyRouteWithRecovery(() => import('./pages/Dashboard'), {
  recoveryKey: 'dashboard',
});
const Maintenance = lazyRouteWithRecovery(() => import('./pages/Maintenance.jsx'), {
  recoveryKey: 'maintenance',
});
const GameDetails = lazyRouteWithRecovery(() => import('./pages/GameDetails'), {
  recoveryKey: 'game-details',
});
const LegacyGameDetails = lazyRouteWithRecovery(() => import('./pages/LegacyGameDetails.jsx'), {
  recoveryKey: 'legacy-game-details',
});
const Games = lazyRouteWithRecovery(() => import('./pages/Games'), {
  recoveryKey: 'games',
});
const Library = lazyRouteWithRecovery(() => import('./pages/Library'), {
  recoveryKey: 'library',
});
const GameDataManager = lazyRouteWithRecovery(() => import('./pages/GameDataManager'), {
  recoveryKey: 'game-data-manager',
});
const DealFinderRoute = lazyRouteWithRecovery(() => import('./pages/DealFinderRoute.jsx'), {
  recoveryKey: 'deal-finder',
});
const CommerceOutboundRoute = lazyRouteWithRecovery(() => import('./pages/CommerceOutboundRoute.jsx'), {
  recoveryKey: 'commerce-outbound',
});
const CommerceOutboundPreview = import.meta.env.DEV
  ? lazyRouteWithRecovery(() => import('./pages/CommerceOutboundPreview.jsx'), {
      recoveryKey: 'commerce-outbound-preview',
    })
  : null;
const DealFinderPreview = import.meta.env.DEV
  ? lazyRouteWithRecovery(() => import('./pages/DealFinderPreview.jsx'), {
      recoveryKey: 'deal-finder-preview',
    })
  : null;
const GameDetailsOffersPreview = import.meta.env.DEV
  ? lazyRouteWithRecovery(() => import('./pages/GameDetailsOffersPreview.jsx'), {
      recoveryKey: 'game-details-offers-preview',
    })
  : null;
const CommunityModerationQueuePreview = import.meta.env.DEV
  ? lazyRouteWithRecovery(() => import('./pages/CommunityModerationQueuePreview.jsx'), {
      recoveryKey: 'community-moderation-preview',
    })
  : null;
const providerInvoicingSyntheticPreviewEnabled = import.meta.env.DEV
  && import.meta.env.VITE_PROVIDER_INVOICING_SYNTHETIC_PREVIEW_ENABLED === 'true';
const ProviderInvoicingPreview = providerInvoicingSyntheticPreviewEnabled
  ? lazyRouteWithRecovery(() => import('./pages/ProviderInvoicingPreview.jsx'), {
      recoveryKey: 'provider-invoicing-preview',
    })
  : null;
const News = lazyRouteWithRecovery(() => import('./pages/News'), {
  recoveryKey: 'news',
});
const NewsArchive = lazyRouteWithRecovery(() => import('./pages/NewsArchive.jsx'), {
  recoveryKey: 'news-archive',
});
const ArticleDetails = lazyRouteWithRecovery(() => import('./pages/ArticleDetails.jsx'), {
  recoveryKey: 'article-details',
});
const Login = lazyRouteWithRecovery(() => import('./pages/Login.jsx'), {
  recoveryKey: 'login',
});
const SignUp = lazyRouteWithRecovery(() => import('./pages/SignUp.jsx'), {
  recoveryKey: 'signup',
});
const AuthCallback = lazyRouteWithRecovery(() => import('./pages/AuthCallback.jsx'), {
  recoveryKey: 'auth-callback',
});
const AccountPrivacy = lazyRouteWithRecovery(() => import('./pages/AccountPrivacy.jsx'), {
  recoveryKey: 'account-privacy',
});
const DeleteAccountConfirmation = lazyRouteWithRecovery(
  () => import('./pages/DeleteAccountConfirmation.jsx'),
  { recoveryKey: 'delete-account-confirmation' },
);
const ResetPassword = lazyRouteWithRecovery(() => import('./pages/ResetPassword.jsx'), {
  recoveryKey: 'reset-password',
});
const LegalIndex = lazyRouteWithRecovery(() => import('./pages/legal/LegalIndex.jsx'), {
  recoveryKey: 'legal-index',
});
const LegalDocumentPage = lazyRouteWithRecovery(() => import('./pages/legal/LegalDocumentPage.jsx'), {
  recoveryKey: 'legal-document',
});
const E2ECheckoutHarness = lazyRouteWithRecovery(() => import('./pages/E2ECheckoutHarness.jsx'), {
  recoveryKey: 'e2e-checkout-harness',
});
const Pricing = lazyRouteWithRecovery(() => import('./pages/Pricing.jsx'), {
  recoveryKey: 'pricing',
});
const Checkout = lazyRouteWithRecovery(() => import('./pages/Checkout.jsx'), {
  recoveryKey: 'checkout',
});
const PaymentMethodSetup = lazyRouteWithRecovery(() => import('./pages/PaymentMethodSetup.jsx'), {
  recoveryKey: 'payment-method-setup',
});
const SupportFaqIndex = lazyRouteWithRecovery(() => import('./pages/support/SupportFaqIndex.jsx'), {
  recoveryKey: 'support-faq-index',
});
const SupportFaqCategory = lazyRouteWithRecovery(() => import('./pages/support/SupportFaqCategory.jsx'), {
  recoveryKey: 'support-faq-category',
});
const SupportFaqArticle = lazyRouteWithRecovery(() => import('./pages/support/SupportFaqArticle.jsx'), {
  recoveryKey: 'support-faq-article',
});
const SupportFaqLegacyArticle = lazyRouteWithRecovery(() => import('./pages/support/SupportFaqLegacyArticle.jsx'), {
  recoveryKey: 'support-faq-legacy-article',
});
const SupportCenter = lazyRouteWithRecovery(() => import('./pages/support/SupportCenter.jsx'), {
  recoveryKey: 'support-center',
});
const IllegalContentReport = lazyRouteWithRecovery(
  () => import('./pages/support/IllegalContentReport.jsx'),
  { recoveryKey: 'illegal-content-report' },
);
const SupportFeedback = lazyRouteWithRecovery(() => import('./pages/support/SupportFeedback.jsx'), {
  recoveryKey: 'support-feedback',
});
const NewsletterUnsubscribe = lazyRouteWithRecovery(() => import('./pages/NewsletterUnsubscribe.jsx'), {
  recoveryKey: 'newsletter-unsubscribe',
});
const Live = lazyRouteWithRecovery(() => import('./pages/Live.jsx'), {
  recoveryKey: 'live',
});
const PublicProfile = lazyRouteWithRecovery(() => import('./pages/PublicProfile.jsx'), {
  recoveryKey: 'public-profile',
});
const TwitchExtensionPanel = lazyRouteWithRecovery(() => import('./pages/extensions/twitch/TwitchExtensionPanel.jsx'), {
  recoveryKey: 'twitch-extension-panel',
});
const TwitchExtensionOverlay = lazyRouteWithRecovery(() => import('./pages/extensions/twitch/TwitchExtensionOverlay.jsx'), {
  recoveryKey: 'twitch-extension-overlay',
});
const TwitchExtensionConfig = lazyRouteWithRecovery(() => import('./pages/extensions/twitch/TwitchExtensionConfig.jsx'), {
  recoveryKey: 'twitch-extension-config',
});
const GameOverPage = lazyRouteWithRecovery(() => import('./components/public/GameOverPage.jsx'), {
  recoveryKey: 'game-over',
});

function RouteLoadingFallback() {
  const { t } = useTranslation();

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center text-sm text-slate-300">
      {t('routing.loading')}
    </div>
  );
}


function LegacyGameDetailsRedirect() {
  const [searchParams] = useSearchParams();
  const slug = searchParams.get('slug');
  const id = searchParams.get('id');
  if (!slug) {
    if (!id) {
      return <Navigate to="/" replace />;
    }
    return <GameDetails />;
  }
  return (
    <Navigate
      to={{ pathname: `/games/${slug}`, search: '' }}
      replace
    />
  );
}

function LegacyArticleDetailsRedirect() {
  const [searchParams] = useSearchParams();
  const slug = searchParams.get('slug');
  if (!slug) {
    return <Navigate to="/news" replace />;
  }
  return <Navigate to={`/news/${slug}`} replace />;
}

function App() {
  return (
    <MaintenanceGate>
      <PublicationGate>
        <Layout>
          <Suspense fallback={<RouteLoadingFallback />}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route
                path="/dashboard"
                element={<Dashboard />}
              />
              <Route path="/games/:slug" element={<GameDetails />} />
              <Route path="/games/legacy/:slug" element={<LegacyGameDetails />} />
              <Route path="/gamedetails" element={<LegacyGameDetailsRedirect />} />
              <Route path="/games" element={<Games />} />
              <Route
                path="/library"
                element={(
                  <ProtectedRoute>
                    <Library />
                  </ProtectedRoute>
                )}
              />
              <Route
                path="/gamedatamanager"
                element={(
                  <ProtectedRoute requiredRoles="admin">
                    <AdminMfaBoundary>
                      <GameDataManager />
                    </AdminMfaBoundary>
                  </ProtectedRoute>
                )}
              />
              <Route path="/deal-finder" element={<DealFinderRoute />} />
              <Route
                path="/deal-finder/outbound/:offerId"
                element={<CommerceOutboundRoute />}
              />
              {DealFinderPreview ? (
                <Route path="/__preview__/deal-finder" element={<DealFinderPreview />} />
              ) : null}
              {CommerceOutboundPreview ? (
                <Route
                  path="/__preview__/commerce-outbound"
                  element={<CommerceOutboundPreview />}
                />
              ) : null}
              {GameDetailsOffersPreview ? (
                <Route path="/__preview__/game-details-offers" element={<GameDetailsOffersPreview />} />
              ) : null}
              {CommunityModerationQueuePreview ? (
                <Route
                  path="/__preview__/community-moderation"
                  element={<CommunityModerationQueuePreview />}
                />
              ) : null}
              {ProviderInvoicingPreview ? (
                <Route
                  path="/__preview__/provider-invoicing"
                  element={<ProviderInvoicingPreview />}
                />
              ) : null}
              <Route path="/news" element={<News />} />
              <Route path="/news/archive" element={<NewsArchive />} />
              <Route path="/news/:slug" element={<ArticleDetails />} />
              <Route path="/articledetails" element={<LegacyArticleDetailsRedirect />} />
              <Route path="/live" element={<Live />} />
              <Route path="/login" element={<Login />} />
              <Route path={STAFF_LOGIN_PATH} element={<Login />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path={PUBLIC_AUTH_CALLBACK_PATH} element={<AuthCallback />} />
              <Route
                path="/account/privacy/delete-confirmation"
                element={<DeleteAccountConfirmation />}
              />
              <Route
                path="/account/privacy"
                element={(
                  <ProtectedRoute>
                    <AccountPrivacy />
                  </ProtectedRoute>
                )}
              />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path={STAFF_RESET_PATH} element={<ResetPassword />} />
              <Route path="/maintenance" element={<Maintenance embedded />} />
              <Route path="/legal" element={<LegalIndex />} />
              <Route path="/legal/:slug" element={<LegalDocumentPage />} />
              <Route path="/__e2e__/checkout" element={<E2ECheckoutHarness />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/support" element={<SupportCenter />} />
              <Route
                path="/support/report-illegal-content"
                element={<IllegalContentReport />}
              />
              <Route path="/support/feedback/:token" element={<SupportFeedback />} />
              <Route path="/newsletter/unsubscribe/:token" element={<NewsletterUnsubscribe />} />
              <Route path="/support/faq" element={<SupportFaqIndex />} />
              {SUPPORT_FAQ_CATEGORY_ORDER.map((category) => (
                <Route
                  key={category}
                  path={buildFaqCategoryPath(category)}
                  element={<SupportFaqCategory category={category} />}
                />
              ))}
              <Route path="/support/faq/:category/:slug" element={<SupportFaqArticle />} />
              <Route path="/support/faq/:slug" element={<SupportFaqLegacyArticle />} />
              <Route path="/u/:handle" element={<PublicProfile />} />
              <Route path="/ext/twitch/config" element={<TwitchExtensionConfig />} />
              <Route path="/ext/twitch/config.html" element={<TwitchExtensionConfig />} />
              <Route path="/ext/twitch/panel" element={<TwitchExtensionPanel />} />
              <Route path="/ext/twitch/panel.html" element={<TwitchExtensionPanel />} />
              <Route path="/ext/twitch/overlay" element={<TwitchExtensionOverlay />} />
              <Route path="/ext/twitch/overlay.html" element={<TwitchExtensionOverlay />} />
              <Route path="/ext/twitch/video_overlay.html" element={<TwitchExtensionOverlay />} />
              <Route
                path="/checkout"
                element={(
                  <ProtectedRoute>
                    <Checkout />
                  </ProtectedRoute>
                )}
              />
              <Route
                path="/payment-methods/new"
                element={(
                  <ProtectedRoute>
                    <PaymentMethodSetup />
                  </ProtectedRoute>
                )}
              />
              <Route path="*" element={<GameOverPage embedded />} />
            </Routes>
          </Suspense>
        </Layout>
      </PublicationGate>
    </MaintenanceGate>
  );
}

export default App;
